import type { App } from 'vue';
import type { Router } from 'vue-router';
import { captureEvent, captureException, setContext, setTag, setUser, withScope } from '@sentry/browser';

import * as Sentry from '@sentry/vue';
import { isEnabled } from '@monolith/legacy/services/features';
import config from '@core/plugins/prismic.config.json';
import { getInjectedContent } from '@modules/core/services/injected-content';
import { GlobalMetaTagName } from '@modules/core/services/initial-state';
import { getApplicationCommitSha } from '@core/utilities/application/appVersion';

const excludedAxiosStatusErrors = [400, 401, 403, 404, 422, 429];
const projectName = 'ankorstore-vue';

interface AxiosLikeError {
  isAxiosError: true;
  code?: string;
  message?: string;
  response?: { status: number };
}

function isAxiosError(error: unknown): error is AxiosLikeError {
  return typeof error === 'object' && error !== null && (error as AxiosLikeError).isAxiosError === true;
}

// `cause` is caller-supplied, so it can be arbitrarily deep or circular. The bound is what
// guarantees termination: a cycle simply exhausts it and the event is reported. It matches the
// default `limit` of Sentry's own LinkedErrors integration, which serialises the same chain.
export const MAX_CAUSE_DEPTH = 5;

// Callers report a failed request as `new Error('<what failed>', { cause: e })`, which leaves the
// AxiosError one level down where the checks below cannot see it. Walk the chain so a wrapped
// error is judged on the request that actually failed.
function findAxiosError(value: unknown): AxiosLikeError | undefined {
  let current = value;

  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth++) {
    if (typeof current !== 'object' || current === null) {
      return undefined;
    }

    if (isAxiosError(current)) {
      return current;
    }

    current = (current as { cause?: unknown }).cause;
  }

  return undefined;
}

// Non-actionable Axios errors: known status codes, cancellations, and Network Errors without a
// response (Safari/WebKit tab suspension).
// See: https://github.com/axios/axios/issues/6898, https://github.com/axios/axios/issues/6647
function isNonActionableAxiosError(error: AxiosLikeError): boolean {
  return (
    excludedAxiosStatusErrors.includes(error.response?.status as number) ||
    error.code === 'ERR_CANCELED' ||
    error.message === 'Request aborted' ||
    error.message === 'canceled' ||
    (error.message === 'Network Error' && !error.response)
  );
}

// TODO: investigate a proper fix for Vite CSS preload failures on stale hashes after deploys.
// Tracked in Sentry: ANKORSTORE-VUE-185HP. Filtering for now to stop quota consumption.
function isUnableToPreloadCssError(originalException: unknown): boolean {
  return originalException instanceof Error && originalException.message.startsWith('Unable to preload CSS for');
}

function isGtmClassConstructorError(event: Sentry.ErrorEvent, originalException: unknown): boolean {
  if (!(originalException instanceof TypeError)) {
    return false;
  }

  if (!/(?:cannot be invoked without|must be invoked with) 'new'/.test(originalException.message)) {
    return false;
  }

  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  return frames.some((frame) => frame.filename?.includes('gtm.js') || frame.abs_path?.includes('googletagmanager.com'));
}

export function sentryBeforeSend(event: Sentry.ErrorEvent, hint: { originalException?: unknown }): Sentry.ErrorEvent | null {
  const { originalException } = hint;

  if (originalException === 'Timeout') {
    return null;
  }

  if (isUnableToPreloadCssError(originalException)) {
    return null;
  }

  if (isGtmClassConstructorError(event, originalException)) {
    return null;
  }

  const axiosError = findAxiosError(originalException);

  if (!axiosError) {
    return event;
  }

  if (isNonActionableAxiosError(axiosError)) {
    return null;
  }

  return event;
}

const recordPrismicCalls = () => {
  if (!('PerformanceObserver' in globalThis)) {
    return undefined;
  }

  const observer = new PerformanceObserver((list) => {
    const prismicApiRequests = Array.from(list.getEntries()).filter((entry) => entry.name.includes(config.prismicApiEndpoint));
    const prismicAssetsRequests = Array.from(list.getEntries()).filter((entry) =>
      entry.name.includes(config.prismicAssetsEndpoint)
    );
    const totalRequests = prismicApiRequests.length + prismicAssetsRequests.length;

    if (totalRequests > 0) {
      Sentry.metrics.increment('prismicRequestsTotal', totalRequests);
      Sentry.metrics.increment('prismicApiRequestsTotal', prismicApiRequests.length);
      Sentry.metrics.increment('prismicAssetsRequestsTotal', prismicAssetsRequests.length);
    }
  });

  observer.observe({ type: 'resource', buffered: true });

  return observer;
};

export const initSentry = (app: App, router?: Router) => {
  const sentryDSN = getInjectedContent<string>(GlobalMetaTagName.SentryDSN);
  const environment = getInjectedContent<string>(GlobalMetaTagName.Environment);
  if (process.env.NODE_ENV !== 'development' && sentryDSN) {
    const dsn = sentryDSN;

    const integrations = [];
    if (router) {
      integrations.push(Sentry.browserTracingIntegration({ router }));
    } else {
      integrations.push(Sentry.browserTracingIntegration());
    }

    if (isEnabled('sentryReplay')) {
      integrations.push(Sentry.replayIntegration());
    }

    Sentry.init({
      app,
      dsn,
      integrations,
      environment,
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.1,

      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['localhost'],

      // Set release name with commit sha
      release: `${projectName}@${getApplicationCommitSha()}`,

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.1,
      beforeSend: sentryBeforeSend,
    });

    recordPrismicCalls();
  }
};

export { captureEvent, captureException, setContext, setTag, setUser, withScope };
